import {
  ChangeEvent,
  Dispatch,
  KeyboardEvent,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react'

import { Button } from 'components/button/Button'
import { TextInput } from 'components/form-elements/TextInput'
import { H4 } from 'components/heading/Heading'

import styles from './FindAddress.module.scss'
import { useAddressSearch } from 'hooks/onboarding/useAddressSearch'
import Spinner from '../spinner/Spinner'
import { FindAddressResults } from './FindAddressResults'
import { Properties, Property } from 'types/onboarding'
import { ArrowButton } from 'components/button/ButtonWithIcon'
import { BackButton } from 'components/back-button/BackButton'

interface FindAddressProps {
  headline: string
  placeholder: string
  setProperty: Dispatch<SetStateAction<Property | undefined>>
  onClickForward: () => void
  isForwardButtonDisabled?: boolean
  isNewAddress?: boolean
  backButtonOnClick?: () => void
}

export const FindAddress = ({
  headline,
  placeholder,
  setProperty,
  onClickForward,
  isNewAddress = false,
  backButtonOnClick,
}: FindAddressProps) => {
  const { state, setState, isLoading, search } = useAddressSearch()

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setState({
        [e.target.name]: e.target.value,
      })
    },
    [setState]
  )

  const isAnyPropertySelected = useMemo(
    () => state.searchResults.some((r) => r.properties.some((p) => p.selected)),
    [state.searchResults]
  )

  const toggleProperty = (resultsIndex: number, propertyIndex: number) => {
    const previousToggleState =
      state.searchResults[resultsIndex].properties[propertyIndex].selected

    const newResults = [...state.searchResults]
    newResults[resultsIndex].properties[propertyIndex].selected =
      !previousToggleState

    setState({ searchResults: newResults })
  }

  const onKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    // If enter, trigger search
    //TODO: fix
    if (e.keyCode === 13) {
      search()
    }
  }

  const onSubmitClick = () => {
    const currentAddresses = [] as Properties

    state.searchResults.forEach((result) => {
      result.properties.forEach((property) => {
        if (property.selected) {
          currentAddresses.push({
            fNumber: property.propertyNumber,
            address: result.address,
            postalCode: result.postalCode,
            city: result.neighborhood,
            selected: true,
          })
        }
      })
    })

    if (currentAddresses.length !== 1) {
      setState({
        error: true,
        errorMessage:
          'Athugaðu að þú getur aðeins valið eitt heimilisfang úr listanum.',
      })
    } else {
      setProperty(currentAddresses[0])
      onClickForward()
    }
  }

  return (
    <div className={styles.searchContainer}>
      <H4 as="h3">{headline}</H4>

      <div className={styles.searchContainer__item}>
        <div className={styles.searchContainer__textInputContainer}>
          <TextInput
            required
            name="searchQuery"
            value={state.searchQuery}
            placeholder={placeholder}
            onKeyUp={onKeyUp}
            onChange={onChange}
            disabled={state.loading}
            autoFocus
            icon="house"
          />
        </div>
        <div>
          <Button onClick={search} className={styles.searchContainer__button}>
            Leita
          </Button>
        </div>
      </div>

      <div>
        {isLoading && <Spinner centered text="Sæki leitarniðurstöður" />}
        {isNewAddress && state.searchResults.length > 0 && (
          <p className={styles.searchContainer__text}>
            Merktu við þau heimilisföng sem eiga að fá rafmagn frá Orkusölunni.
          </p>
        )}

        <FindAddressResults
          results={state.searchResults}
          toggleProperty={toggleProperty}
        />

        {state.searchResults.length > 0 && (
          <div className={styles.searchContainer__buttonContainer}>
            {backButtonOnClick && (
              <BackButton onClick={backButtonOnClick}>Til baka</BackButton>
            )}

            <ArrowButton
              onClick={onSubmitClick}
              disabled={!isAnyPropertySelected}
            >
              Áfram
            </ArrowButton>
          </div>
        )}

        {state.error && (
          <H4 className={styles.searchContainer__error}>
            {state.errorMessage}
          </H4>
        )}

        {state.noResults && !state.error && (
          <H4>
            Engar niðurstöður fundust! Vinsamlegast sláið inn bæði götunúmer
            ásamt húsnúmeri.
          </H4>
        )}
      </div>
    </div>
  )
}
